// Copyright 2025, University of Maryland, All Rights Reserved

type CoverageLevel = 'H' | 'M' | 'L' | null

interface GapItem {
  clo_id: number
  statement: string
  requiring_course_level: CoverageLevel
  prerequisite_course_level: CoverageLevel
  gap_severity: 'Critical' | 'Moderate' | 'Minor' | 'None'
  gap_explanation: string
}

export interface PrerequisiteGapAnalysisProps {
  gap_analysis: GapItem[]
  readiness_rating: 'Adequate' | 'Partial' | 'Inadequate'
  readiness_explanation: string
  prerequisite_label?: string
  requiring_label?: string
}

const READINESS_LABEL: Record<string, string> = {
  Adequate: 'Adequate',
  Partial: 'Partial',
  Inadequate: 'Inadequate',
}

const LEVEL_LABEL: Record<string, string> = { H: 'High', M: 'Medium', L: 'Low' }

const SEVERITY_CONFIG = {
  Critical: { badge: 'bg-red-100 text-red-800 border border-red-200', dot: 'bg-red-500' },
  Moderate: { badge: 'bg-orange-100 text-orange-800 border border-orange-200', dot: 'bg-orange-400' },
  Minor:    { badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200', dot: 'bg-yellow-400' },
  None:     { badge: 'bg-green-100 text-green-800 border border-green-200', dot: 'bg-green-500' },
}

function CoverageChip({ level }: { level: CoverageLevel }) {
  if (!level) return <span className="text-xs text-gray-400 italic">Not present</span>
  const colors: Record<string, string> = {
    H: 'bg-[#E21833] text-white',
    M: 'bg-[#FFD520] text-gray-800',
    L: 'bg-[#D3D3D3] text-gray-700',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colors[level]}`}>
      {level} — {LEVEL_LABEL[level]}
    </span>
  )
}

export function PrerequisiteGapAnalysis({
  gap_analysis,
  readiness_rating,
  readiness_explanation,
  prerequisite_label = 'Prerequisite Course',
  requiring_label = 'Requiring Course',
}: PrerequisiteGapAnalysisProps) {
  const criticalCount = gap_analysis.filter(g => g.gap_severity === 'Critical').length
  const moderateCount = gap_analysis.filter(g => g.gap_severity === 'Moderate').length
  const noneCount = gap_analysis.filter(g => g.gap_severity === 'None').length

  return (
    <div className="space-y-6">

      {/* Readiness rating card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-bold text-gray-900">Prerequisite Readiness</h3>
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                {READINESS_LABEL[readiness_rating]}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">{readiness_explanation}</p>

            {/* Gap summary chips */}
            <div className="flex flex-wrap gap-2 text-xs">
              {criticalCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 font-medium">
                  {criticalCount} Critical gap{criticalCount !== 1 ? 's' : ''}
                </span>
              )}
              {moderateCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 font-medium">
                  {moderateCount} Moderate gap{moderateCount !== 1 ? 's' : ''}
                </span>
              )}
              {noneCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 font-medium">
                  {noneCount} adequate
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gap analysis table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Gap Analysis by CLO</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            For each CLO required by <span className="font-medium text-gray-700">{requiring_label}</span>,
            how well does <span className="font-medium text-gray-700">{prerequisite_label}</span> prepare students?
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLO Statement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {requiring_label}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {prerequisite_label}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Explanation</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {gap_analysis.map(item => {
                const sev = SEVERITY_CONFIG[item.gap_severity] ?? SEVERITY_CONFIG.None
                return (
                  <tr key={item.clo_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-500">{item.clo_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                      <p className="line-clamp-3" title={item.statement}>{item.statement}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <CoverageChip level={item.requiring_course_level} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <CoverageChip level={item.prerequisite_course_level} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${sev.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                        {item.gap_severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-sm">
                      <p className="line-clamp-3" title={item.gap_explanation}>{item.gap_explanation}</p>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Gap Severity Legend</p>
        <div className="flex flex-wrap gap-3">
          {(Object.entries(SEVERITY_CONFIG) as [keyof typeof SEVERITY_CONFIG, typeof SEVERITY_CONFIG[keyof typeof SEVERITY_CONFIG]][]).map(([key, cfg]) => (
            <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.badge}`}>
              <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
              {key}
              {key === 'Critical' && ' — required but not taught in prerequisite'}
              {key === 'Moderate' && ' — partially covered'}
              {key === 'Minor' && ' — small gap'}
              {key === 'None' && ' — adequately covered'}
            </span>
          ))}
        </div>
      </div>

    </div>
  )
}
